import { useState, useRef } from 'react'
import { useDisclosure } from '@chakra-ui/react'
import { toast } from 'sonner'
import { detectFormatFromFilename } from '@/lib/utils/filename'
import { validateFileImport, extractFilenameFromUrl, rewriteGitHubUrl } from '@/lib/utils/file-import'
import type { ShareFormat } from '@core/types/share'

interface UseFileImportOptions {
  onContentLoaded: (content: string, filename?: string, format?: ShareFormat) => void
}

/**
 * Manages file import from local filesystem and URL loading.
 * Owns the hidden file input ref, URL modal state, and all related handlers.
 */
export function useFileImport({ onContentLoaded }: UseFileImportOptions) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [url, setUrl] = useState('')
  const [isLoadingUrl, setIsLoadingUrl] = useState(false)
  const {
    isOpen: isUrlOpen,
    onOpen: onUrlOpen,
    onClose: onUrlClose,
  } = useDisclosure()

  const handleFileUpload = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const validation = validateFileImport(file.name, file.size)
    if (!validation.valid) {
      toast.error(validation.error)
      e.target.value = ''
      return
    }

    try {
      const text = await file.text()
      onContentLoaded(text, file.name, validation.format)
      toast.success(`Loaded ${file.name}`)
    } catch {
      toast.error('Failed to read file')
    }

    e.target.value = ''
  }

  const handleUrlSubmit = async () => {
    if (!url.trim()) {
      toast.error('Please enter a URL')
      return
    }

    setIsLoadingUrl(true)
    try {
      const fetchUrl = rewriteGitHubUrl(url.trim())

      const response = await fetch(fetchUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`)
      }

      const text = await response.text()
      const filename = extractFilenameFromUrl(fetchUrl)
      const format = filename ? detectFormatFromFilename(filename) : 'md'
      onContentLoaded(text, filename, format)
      toast.success('Content loaded from URL')
      onUrlClose()
      setUrl('')
    } catch {
      toast.error('Failed to fetch content from URL')
    } finally {
      setIsLoadingUrl(false)
    }
  }

  return {
    fileInputRef,
    handleFileUpload,
    handleFileChange,
    url,
    setUrl,
    isLoadingUrl,
    isUrlOpen,
    onUrlOpen,
    onUrlClose,
    handleUrlSubmit,
  }
}
